import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';

const STORAGE_KEY = 'dashboard_filters_shared';
const PAGE_FILTERS_KEY = 'page_filters';

const detectPreset = (startDate, endDate) => {
  if (startDate === null && endDate === null) return 'alltime';
  if (!startDate || !endDate) return 'custom';

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const now = dayjs();

  if (start.isSame(now.startOf('day'), 'minute') && end.isSame(now.endOf('day'), 'minute')) {
    return 'today';
  }

  const yesterday = now.subtract(1, 'day');
  if (start.isSame(yesterday.startOf('day'), 'minute') && end.isSame(yesterday.endOf('day'), 'minute')) {
    return 'yesterday';
  }

  if (start.isSame(now.startOf('week'), 'day') && end.isSame(now, 'day')) {
    return 'weekly';
  }

  if (start.isSame(now.startOf('year'), 'day') && end.isSame(now, 'day')) {
    return 'ytd';
  }

  if (start.isSame(now.startOf('month'), 'day') && end.isSame(now, 'day')) {
    return 'monthly';
  }

  if (start.isSame(now.subtract(1, 'year'), 'day') && end.isSame(now, 'day')) {
    return 'yearly';
  }

  return 'custom';
};

const getDefaultFilters = () => ({
  startDate: dayjs().startOf('day').toDate(),
  endDate: dayjs().endOf('day').toDate(),
  preset: 'today',
  clientIds: [],
  operatorIds: [],
  brandIds: []
});

const serializeFilters = (filters) => {
  return JSON.stringify({
    ...filters,
    startDate: filters.startDate ? filters.startDate.toISOString() : null,
    endDate: filters.endDate ? filters.endDate.toISOString() : null
  });
};

const deserializeFilters = (stored) => {
  try {
    const parsed = JSON.parse(stored);
    return {
      ...parsed,
      startDate: parsed.startDate ? new Date(parsed.startDate) : null,
      endDate: parsed.endDate ? new Date(parsed.endDate) : null
    };
  } catch {
    return null;
  }
};

function usePersistedFilters() {
  const storageKey = STORAGE_KEY;

  const [filters, setFiltersState] = useState(() => {
    if (typeof window === 'undefined') {
      return getDefaultFilters();
    }

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const deserialized = deserializeFilters(stored);
      if (deserialized) {
        return deserialized;
      }
    }

    return getDefaultFilters();
  });

  useEffect(() => {
    localStorage.setItem(storageKey, serializeFilters(filters));
  }, [filters, storageKey]);

  const setFilters = useCallback((newFilters) => {
    setFiltersState((prev) => {
      if (typeof newFilters === 'function') {
        return newFilters(prev);
      }
      return { ...prev, ...newFilters };
    });
  }, []);

  const clearFilters = useCallback(() => {
    const defaults = getDefaultFilters();
    setFiltersState(defaults);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  const setDateRange = useCallback((startDate, endDate, preset = null) => {
    const detectedPreset = preset || detectPreset(startDate, endDate);

    setFiltersState((prev) => ({
      ...prev,
      startDate,
      endDate,
      preset: detectedPreset
    }));
  }, []);

  const setEntityFilters = useCallback((entityFilters) => {
    setFiltersState((prev) => ({
      ...prev,
      clientIds: entityFilters.clientIds ?? prev.clientIds,
      clientNames: entityFilters.clientNames ?? prev.clientNames ?? [],
      operatorIds: entityFilters.operatorIds ?? prev.operatorIds,
      operatorNames: entityFilters.operatorNames ?? prev.operatorNames ?? [],
      brandIds: entityFilters.brandIds ?? prev.brandIds,
      brandNames: entityFilters.brandNames ?? prev.brandNames ?? []
    }));
  }, []);

  const setEntityFiltersForNavigation = useCallback((entityFilters) => {
    setFiltersState((prev) => ({
      ...prev,
      clientIds: entityFilters.clientIds || [],
      clientNames: entityFilters.clientNames || [],
      operatorIds: entityFilters.operatorIds || [],
      operatorNames: entityFilters.operatorNames || [],
      brandIds: entityFilters.brandIds || [],
      brandNames: entityFilters.brandNames || []
    }));
  }, []);

  return {
    filters,
    setFilters,
    clearFilters,
    setDateRange,
    setEntityFilters,
    setEntityFiltersForNavigation
  };
}

// Hook for page-specific filters that persist separately
export function usePageFilters(pageKey, defaultFilters = {}) {
  const storageKey = `${PAGE_FILTERS_KEY}_${pageKey}`;

  const [pageFilters, setPageFiltersState] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultFilters;
    }

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        return { ...defaultFilters, ...JSON.parse(stored) };
      } catch {
        return defaultFilters;
      }
    }

    return defaultFilters;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(pageFilters));
  }, [pageFilters, storageKey]);

  const setPageFilters = useCallback((newFilters) => {
    setPageFiltersState((prev) => {
      if (typeof newFilters === 'function') {
        return newFilters(prev);
      }
      return { ...prev, ...newFilters };
    });
  }, []);

  const clearPageFilters = useCallback(() => {
    setPageFiltersState(defaultFilters);
    localStorage.removeItem(storageKey);
  }, [storageKey, defaultFilters]);

  return {
    pageFilters,
    setPageFilters,
    clearPageFilters
  };
}

export default usePersistedFilters;
